import React from 'react';
import { useTranslation } from 'react-i18next';
import { getKakaoLoginUrl } from '../../services/kakaoAuth';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleKakaoLogin = () => {
    const clientId = process.env.REACT_APP_KAKAO_CLIENT_ID || '';
    const redirectUri = process.env.REACT_APP_KAKAO_REDIRECT_URI || `${window.location.origin}/auth/kakao/callback`;

    // 로그인 후 돌아올 페이지 저장
    localStorage.setItem('kakao_login_redirect', window.location.pathname + window.location.search);

    const kakaoLoginUrl = getKakaoLoginUrl({
      clientId,
      redirectUri,
    });

    // Kakao 로그인 페이지로 리다이렉트
    window.location.href = kakaoLoginUrl;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.login')}</h2>
          <p className="text-gray-600">{t('auth.welcome')}</p>
        </div>

        {/* Kakao Login Button */}
        <button
          onClick={handleKakaoLogin}
          className="w-full bg-[#FEE500] hover:bg-[#FDD835] text-[#191919] font-medium py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-sm hover:shadow-md"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3zm5.907 8.06l1.47-1.424a.472.472 0 0 0-.656-.678l-1.928 1.866V9.282a.472.472 0 0 0-.944 0v2.557a.471.471 0 0 0 0 .222V13.5a.472.472 0 0 0 .944 0v-1.363l.427-.413 1.428 2.033a.472.472 0 1 0 .773-.543l-1.514-2.155zm-2.958 1.924h-1.46V9.297a.472.472 0 0 0-.943 0v4.159c0 .26.21.472.471.472h1.932a.472.472 0 1 0 0-.944zm-5.857-1.092l.696-1.707.638 1.707H9.092zm2.523.488l.002-.016a.469.469 0 0 0-.127-.32l-1.046-2.8a.69.69 0 0 0-.627-.431.69.69 0 0 0-.627.431l-1.168 3.124a.472.472 0 0 0 .874.327l.263-.701h1.985l.238.697a.472.472 0 0 0 .887-.311h.346zM7.09 9.291a.472.472 0 0 0-.471-.472H3.762a.472.472 0 1 0 0 .944h1.032v3.687a.472.472 0 0 0 .944 0V9.763h1.352a.472.472 0 0 0 .472-.472h-.472z"/>
          </svg>
          {t('auth.kakaoStart')}
        </button>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">{t('auth.or')}</span>
          </div>
        </div>

        {/* Guest Continue */}
        <button
          onClick={onClose}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-4 px-6 rounded-xl transition-all duration-200"
        >
          {t('auth.continueGuest')}
        </button>

        {/* Terms Notice */}
        <p className="text-xs text-gray-500 text-center mt-6">
          {t('auth.agreePrefix')}{' '}
          <a href="/terms" className="text-blue-600 hover:underline">
            {t('auth.terms')}
          </a>
          {' '}{t('auth.and')}{' '}
          <a href="/privacy" className="text-blue-600 hover:underline">
            {t('auth.privacy')}
          </a>
          {t('auth.agreeSuffix')}
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
